import Seo from "@/components/Seo";
import A11ySettings from "@/components/A11ySettings";

export default function AccessibilityPage() {
    return (
        <>
            <Seo
                title="Accessibility Statement — FinCalc"
                description="FinCalc's commitment to WCAG 2.1 Level AA and AAA accessibility. How to report an issue and what assistive technology we support."
                canonical="/accessibility"
            />
            <section className="container-editorial py-16 max-w-3xl">
                <div className="text-[11px] uppercase tracking-[0.2em] text-accent">Legal</div>
                <h1 className="font-serif text-5xl lg:text-6xl text-primary mt-3 leading-tight">
                    Accessibility Statement
                </h1>
                <p className="mt-3 text-sm text-muted-foreground">Last updated: February 2026</p>

                <div className="mt-6 p-5 rounded-xl border border-border bg-card flex items-center justify-between gap-4">
                    <p className="text-sm text-foreground/80">
                        Need immediate adjustments? Open accessibility preferences.
                    </p>
                    <A11ySettings />
                </div>

                <div className="mt-8 prose-editorial">
                    <h2>Our commitment</h2>
                    <p>
                        FinCalc is committed to providing a website that is accessible to the
                        widest possible audience, regardless of technology or ability. We aim to
                        comply with <strong>WCAG 2.1 Level AA</strong> and target{" "}
                        <strong>Level AAA</strong> where feasible. Financial decisions affect
                        everyone — our calculators should too.
                    </p>

                    <h2>Conformance status</h2>
                    <p>
                        The Web Content Accessibility Guidelines (WCAG) define requirements for
                        designers and developers to improve accessibility for people with
                        disabilities. FinCalc is{" "}
                        <strong>partially conformant with WCAG 2.1 Level AAA</strong>. Partially
                        conformant means that some parts of the content do not fully conform to
                        the accessibility standard. We document specific exceptions below.
                    </p>

                    <h2>Features we&apos;ve built in</h2>
                    <ul>
                        <li><strong>Skip-to-content</strong> link as the first focusable element on every page.</li>
                        <li><strong>Full keyboard navigation</strong> for all interactive elements, with visible focus indicators at 3px accent-gold outline.</li>
                        <li><strong>Screen-reader announcements</strong> of calculator result changes via <code>aria-live=&quot;polite&quot;</code>.</li>
                        <li><strong>Reduced-motion support</strong> — all entrance animations and number counters honour the <code>prefers-reduced-motion</code> OS setting.</li>
                        <li><strong>Dyslexia-friendly font</strong> toggle — switches to Atkinson Hyperlegible.</li>
                        <li><strong>High-contrast theme</strong> toggle for low-vision readers.</li>
                        <li><strong>Larger text</strong> toggle that scales the whole UI.</li>
                        <li><strong>Touch targets</strong> of at least 44×44 px throughout.</li>
                        <li><strong>Semantic HTML</strong>: a single <code>&lt;h1&gt;</code> per page, logical heading order, proper landmarks (<code>header</code>, <code>main</code>, <code>nav</code>, <code>footer</code>).</li>
                        <li><strong>Form labelling</strong> — every input has an associated <code>&lt;label&gt;</code> and descriptive placeholder.</li>
                        <li><strong>Alternative text</strong> for all informative images; decorative images are marked <code>aria-hidden</code>.</li>
                        <li><strong>Chart data tables</strong> — screen-reader-only summaries accompany every visualisation.</li>
                        <li><strong>Keyboard shortcuts</strong>: press <kbd>?</kbd> to open the help overlay.</li>
                        <li>
                            <strong>Colour contrast</strong>: body text meets AAA 7:1 on cream; interactive elements meet AAA for non-text 3:1.
                        </li>
                    </ul>

                    <h2>Assistive technologies we test with</h2>
                    <ul>
                        <li>NVDA 2024+ (Windows) with Chrome / Firefox</li>
                        <li>VoiceOver (macOS 14+ / iOS 17+) with Safari</li>
                        <li>TalkBack (Android 14+) with Chrome</li>
                        <li>Keyboard-only navigation across all supported browsers</li>
                        <li>200% zoom without horizontal scrolling</li>
                    </ul>

                    <h2>Known limitations</h2>
                    <ul>
                        <li>
                            <strong>Interactive charts</strong>: our Recharts visualisations include
                            an <code>aria-label</code> summary and a screen-reader-only data table
                            below the chart, but the chart itself is a canvas/SVG that screen readers
                            cannot interrogate point-by-point.
                        </li>
                        <li>
                            <strong>Third-party ads</strong>: when Google AdSense ads load, their
                            content is outside our control. We follow Google&apos;s policy on ad
                            labelling and placement.
                        </li>
                    </ul>

                    <h2>Feedback and contact</h2>
                    <p>
                        We welcome feedback on the accessibility of FinCalc. If you encounter an
                        accessibility barrier or have a suggestion, please email us at{" "}
                        <a href="mailto:access@afincalc.com">access@afincalc.com</a> or use
                        the <a href="/contact">contact form</a>. We aim to respond within five
                        business days.
                    </p>

                    <h2>Formal complaints</h2>
                    <p>
                        If your feedback is not handled to your satisfaction, you may file a
                        complaint with a relevant authority in your jurisdiction (for example, the
                        US Department of Justice ADA section, the Equality and Human Rights
                        Commission in the UK, or the European Commission&apos;s accessibility
                        unit).
                    </p>

                    <h2>Technical specifications</h2>
                    <p>
                        Accessibility of FinCalc relies on the following technologies to work with
                        your web browser and any assistive technologies installed:
                        HTML, WAI-ARIA, CSS, and JavaScript. These technologies are relied upon
                        for conformance.
                    </p>
                </div>
            </section>
        </>
    );
}
